import { formatMoney, formatPhone } from '@dorixona/utils'
import { Icon } from '../../ui/Icon'
import { initials } from '../auth/model'
import type { Customer, CustomerLang } from '../customers/api'

const LANG_TEXT: Record<CustomerLang, string> = {
  ru: 'русский',
  uz: 'узбекский',
  en: 'английский',
}

export interface CustomerPanelProps {
  /** Карточка клиента; её нет без права на клиентов или если клиента удалили. */
  customer: Customer | undefined
  /** Имя и телефон из самого заказа: их видно и без права на клиентов. */
  name: string
  phone: string
  balance: number
  /** Сколько всего заказов у клиента; null — посчитать нечем. */
  ordersCount: number | null
  /** Карточка клиента открывается, только когда её удалось загрузить. */
  onOpen: () => void
}

/**
 * Клиент рядом с заказом: кто он и сколько у него баллов. Без права на
 * клиентов — имя и телефон из заказа. Клиента могли удалить: тогда в заказе
 * не остаётся ни имени, ни телефона, и панель говорит об этом прямо.
 */
export function CustomerPanel({
  customer,
  name,
  phone,
  balance,
  ordersCount,
  onOpen,
}: CustomerPanelProps) {
  const who = customer?.name || name
  const tel = customer?.phone || phone
  return (
    <section className="ui-card" aria-labelledby="od-customer">
      <div className="ui-card-head">
        <h3 id="od-customer">Клиент</h3>
      </div>
      {who || tel ? (
        <>
          <div className="ui-card-body od-customer">
            <div className="ui-person">
              <span className="ui-avatar od-avatar" aria-hidden="true">
                {initials(who || '?')}
              </span>
              <span className="txt">
                <b>{who || '—'}</b>
                <span className="od-phone">{formatPhone(tel) || '—'}</span>
              </span>
            </div>
            <div className="ui-facts">
              {customer ? (
                <>
                  <div>
                    <span className="k">Баланс баллов</span>
                    <span className="v od-num">{formatMoney(balance)}</span>
                  </div>
                  {/* Номер телефона — он же карта лояльности: по нему копят баллы на кассе. */}
                  <div>
                    <span className="k">Карта лояльности</span>
                    <span className="v od-num">{formatPhone(tel) || '—'}</span>
                  </div>
                </>
              ) : null}
              {ordersCount === null ? null : (
                <div>
                  <span className="k">Заказов всего</span>
                  <span className="v od-num">{ordersCount}</span>
                </div>
              )}
              {customer ? (
                <>
                  <div>
                    <span className="k">Статус</span>
                    <span className="v">
                      {customer.blocked ? (
                        <span className="ui-status" data-tone="danger">
                          заблокирован
                        </span>
                      ) : (
                        <span className="ui-status" data-tone="success">
                          активен
                        </span>
                      )}
                    </span>
                  </div>
                  <div>
                    <span className="k">Язык приложения</span>
                    <span className="v">{LANG_TEXT[customer.lang]}</span>
                  </div>
                </>
              ) : null}
            </div>
          </div>
          {customer ? (
            <div className="ui-card-foot">
              <button type="button" className="ui-btn" data-variant="secondary" onClick={onOpen}>
                Карточка клиента
              </button>
            </div>
          ) : null}
        </>
      ) : (
        <div className="ui-empty">
          <Icon name="i-users" />
          <b>Клиент удалён</b>
          <p>Заказ остался в истории</p>
        </div>
      )}
    </section>
  )
}
